import { NSARouterMakerResult } from "namirasoft-account-react";
import { GatewayFullRow, GatewayGuardRailInputRow, GatewayInferencerType, GatewayInputRow, GatewayKnowledgebaseInputRow, GatewayRow, InferencerType } from "namirasoft-inference";
import { NSBox, NSBoxString, NSBoxTextArea, NSColumn, NSRepeater, NSRow, NSSpace, NSSpaceSizeType } from "namirasoft-site-react";
import { Component, createRef } from "react";
import GatewayGuardRailLine from "../components/GatewayGuardRailLine";
import GatewayKnowledgebaseLine from "../components/GatewayKnowledgebaseLine";
import InferencerCommonFields from "../components/InferencerCommonFields";
import InferencerGraph from "../components/InferencerGraph";
import InferencerLoader from "../components/InferencerLoader";
import InferencerTypeID from "../components/InferencerTypeID";
import InferencerVisualizer from "../components/InferencerVisualizer";
import { InferenceEntities } from "../InferenceEntities";
import { InferenceInfo } from "../InferenceInfo";
import { InferenceLayout } from '../InferenceLayout';
import { InferenceRouterProps } from "../InferenceRouterProps";

interface GatewayEditPageProps extends NSARouterMakerResult, InferenceRouterProps
{
    isEdit: boolean;
}

export default class GatewayEditPage extends Component<GatewayEditPageProps>
{
    NSRepeater_GuardRails = createRef<NSRepeater<GatewayGuardRailLine, GatewayGuardRailInputRow>>();
    NSRepeater_Knowledgebases = createRef<NSRepeater<GatewayKnowledgebaseLine, GatewayKnowledgebaseInputRow>>();
    NSBoxString_Name = createRef<NSBoxString>();
    InferencerTypeID_Inferencer = createRef<InferencerTypeID<GatewayInferencerType>>();
    InferencerCommonFields_Ids = createRef<InferencerCommonFields>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    override render()
    {
        return (
            <InferenceLayout<GatewayRow, GatewayInputRow>
                {...this.props}
                action={{ description: this.props.isEdit ? InferenceInfo.gateway.edit : InferenceInfo.gateway.create }}
                pages={{
                    entity: InferenceEntities.gateway(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): GatewayInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let inferencer = this.InferencerTypeID_Inferencer.current?.getValue() ?? { inferencer_type: GatewayInferencerType.MODEL, inferencer_id: "" };
                                        let inferencer_type = inferencer.inferencer_type;
                                        let inferencer_id = inferencer.inferencer_id;
                                        let { validator_id, budget_id, memory_id, wiper_id, log_group_id } = this.InferencerCommonFields_Ids.current?.getValue() ?? InferencerCommonFields.EMPTY;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? null;
                                        let guard_rails = this.NSRepeater_GuardRails.current?.getValue() ?? [];
                                        let knowledgebases = this.NSRepeater_Knowledgebases.current?.getValue() ?? [];

                                        return {
                                            workspace_id: null,
                                            name,
                                            inferencer_type,
                                            inferencer_id,
                                            validator_id,
                                            budget_id,
                                            memory_id,
                                            wiper_id,
                                            log_group_id,
                                            description,
                                            guard_rails,
                                            knowledgebases,
                                            gateway_category: [],
                                            gateway_field: [],
                                            gateway_tag: [],
                                        };
                                    },
                                    setRow: (row: GatewayRow) =>
                                    {
                                        let full = row as GatewayFullRow;

                                        this.NSBoxString_Name.current?.setValue(full.name);
                                        this.InferencerTypeID_Inferencer.current?.setValue({ inferencer_type: full.inferencer_type, inferencer_id: full.inferencer_id });
                                        this.InferencerCommonFields_Ids.current?.setValue(full);
                                        this.NSBoxTextArea_Description.current?.setValue(full.description);
                                        this.NSRepeater_GuardRails.current?.setValue((full.guard_rails ?? []).map(g => ({
                                            guard_rail_id: g.guard_rail_id,
                                        })));
                                        this.NSRepeater_Knowledgebases.current?.setValue((full.knowledgebases ?? []).map(k => ({
                                            knowledgebase_id: k.knowledgebase_id,
                                        })));
                                    }
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Guard Rails",
                                            clicked: true,
                                            getContent: () => (
                                                <NSRepeater<GatewayGuardRailLine, GatewayGuardRailInputRow>
                                                    ref={this.NSRepeater_GuardRails}
                                                    title="Guard Rails"
                                                    required={false}
                                                    createItem={(gIndex: number, lIndex: number, ref: React.RefObject<GatewayGuardRailLine>, onChange: () => void, onDelete: undefined | ((index: number) => void)) =>
                                                    {
                                                        return <GatewayGuardRailLine
                                                            ref={ref}
                                                            {...this.props}
                                                            key={`GatewayGuardRailLine_${gIndex}`}
                                                            index={lIndex}
                                                            deletable={true}
                                                            onChanged={() => { onChange(); }}
                                                            onDelete={() => { onDelete?.(lIndex); }}
                                                        />;
                                                    }}
                                                    isEmpty={function (_, value: GatewayGuardRailInputRow | null): boolean
                                                    {
                                                        return !GatewayGuardRailLine.IS_DIRTY(value);
                                                    }}
                                                    getValue={function (item: GatewayGuardRailLine, checkError: boolean): GatewayGuardRailInputRow | null
                                                    {
                                                        return item.getValue(checkError);
                                                    }}
                                                    setValue={function (item: GatewayGuardRailLine, _: number, value: GatewayGuardRailInputRow | null): void
                                                    {
                                                        if (value)
                                                            item.setValue(value);
                                                    }}
                                                    style={{ width: "100%", maxWidth: NSBox.width.double }}
                                                />
                                            )
                                        },
                                        {
                                            title: "Knowledgebases",
                                            clicked: true,
                                            getContent: () => (
                                                <NSRepeater<GatewayKnowledgebaseLine, GatewayKnowledgebaseInputRow>
                                                    ref={this.NSRepeater_Knowledgebases}
                                                    title="Knowledgebases"
                                                    required={false}
                                                    createItem={(gIndex: number, lIndex: number, ref: React.RefObject<GatewayKnowledgebaseLine>, onChange: () => void, onDelete: undefined | ((index: number) => void)) =>
                                                    {
                                                        return <GatewayKnowledgebaseLine
                                                            ref={ref}
                                                            {...this.props}
                                                            key={`GatewayKnowledgebaseLine_${gIndex}`}
                                                            index={lIndex}
                                                            deletable={true}
                                                            onChanged={() => { onChange(); }}
                                                            onDelete={() => { onDelete?.(lIndex); }}
                                                        />;
                                                    }}
                                                    isEmpty={function (_, value: GatewayKnowledgebaseInputRow | null): boolean
                                                    {
                                                        return !GatewayKnowledgebaseLine.IS_DIRTY(value);
                                                    }}
                                                    getValue={function (item: GatewayKnowledgebaseLine, checkError: boolean): GatewayKnowledgebaseInputRow | null
                                                    {
                                                        return item.getValue(checkError);
                                                    }}
                                                    setValue={function (item: GatewayKnowledgebaseLine, _: number, value: GatewayKnowledgebaseInputRow | null): void
                                                    {
                                                        if (value)
                                                            item.setValue(value);
                                                    }}
                                                    style={{ width: "100%", maxWidth: NSBox.width.double }}
                                                />
                                            )
                                        },
                                        {
                                            title: "Common",
                                            clicked: true,
                                            getContent: () => (
                                                <InferencerCommonFields ref={this.InferencerCommonFields_Ids} {...this.props} info_entity="gateway" />
                                            ),
                                        },
                                    ]
                                }
                            },
                            getChildrenAfter: (id, _row) => id
                                ? <>
                                    <NSSpace size={NSSpaceSizeType.SMALL} />
                                    <InferencerLoader
                                        {...this.props}
                                        inferencer_type={InferencerType.GATEWAY}
                                        inferencer_id={id}
                                        render={({ tree, loading, error }) => (
                                            <>
                                                <InferencerGraph tree={tree} loading={loading} error={error} />
                                                <NSSpace size={NSSpaceSizeType.SMALL} />
                                                <InferencerVisualizer tree={tree} loading={loading} error={error} />
                                            </>
                                        )}
                                    />
                                </>
                                : null,
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            required
                            title="Name"
                            style={{ width: NSBox.width.double }}
                            info={{ link: "https://namirasoft.com/inference/console-guide/gateway/#field-name" }}
                        />
                    </NSRow>
                    <NSRow>
                        <InferencerTypeID<GatewayInferencerType>
                            ref={this.InferencerTypeID_Inferencer}
                            {...this.props}
                            typeTitle="Inferencer Type"
                            idTitle="Inferencer ID"
                            required
                            getEnumObject={() => GatewayInferencerType}
                            defaultType={GatewayInferencerType.MODEL}
                            info={{ link: "https://namirasoft.com/inference/console-guide/gateway/#field-inferencer" }}
                        />
                    </NSRow>
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        title="Description"
                        required={false}
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/gateway/#field-description" }}
                    />
                </NSColumn>
            </InferenceLayout>
        );
    }
}